/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    EpisodeVodItem,
    MovieVodItem,
    PlayableVodItem,
    ScreenType,
    VodItemType,
} from 'client-code/vod-browse';

import { DeviceModel } from '../../core/device';
import { Locale, LocaleKeys } from '../../core/locale';
import { Log } from '../../core/logging';
import { CursorManager, KeyManager } from '../../core/navigation';
import { ScreenController } from '../../core/navigation/ScreenController';
import { NetworkManager } from '../../core/network/NetworkManager';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { TGalleryGridActivePageResponse, TGalleryScreenState } from '../../core/types';
import { GalleryScreenComponent } from '../../core/usecases';
import { CcbSwitch } from '../../core/utils/ccb';
import { CcbSwitchName } from '../../core/utils/ccb/enums';
import { GridTypes } from '../../core/utils/GridTypes';
import { gallerySetCaretInvisible } from '../../store/galleryscreen/galleryscreen.reducers';
import { setGridItemFocusAction, setGridItemsAction } from '../../store/grid/grid.actions';
import { resetGrid } from '../../store/grid/grid.reducers';
import { clearActiveInteractiveComponent } from '../../store/interactivelayer/interactiveLayer.reducers';
import {
    showErrorOverlayGalleryScreen,
    showNetworkWhisperGalleryScreen,
} from '../../store/overlay/galleryscreennonetworkoverlay/galleryscreennonetworkoverlay.reducers';
import { hideOverlayDialog } from '../../store/overlaydialog/overlaydialog.reducers';
import { hideWhisper } from '../../store/whisper/whisper.reducer';
import { ClockTypes } from '../Clock';
import { AntiburnOsd, Mousetrap, ScreenHeader } from '../common';
import Grid from '../common/Grid/Grid';
import { VodPreview } from '../common/VodPreview/VodPreview';
import { VodScreenType } from '../common/VodPreview/VodPreviewProps';
import { Whisper } from '../Whisper';
import GalleryGridSkeleton from './GalleryItem/GalleryGridSkeleton';
import GalleryItem from './GalleryItem/GalleryItem';
import { InteractiveLayer } from './InteractiveLayer';

import './GalleryScreen.scss';

const GalleryScreen: FunctionComponent = (): JSX.Element => {
    const TAG = 'GalleryScreen';

    const dispatch = useTivoDispatch();
    const galleryUrl = 'gallery';
    const isVodPreviewEnabled = CcbSwitch.getInstance().isEnabledAndOverriden(
        CcbSwitchName.ENABLE_VOD_PREVIEW_REMOVE_IN_KST_14762,
    );

    const parentVodItemIdRef = useRef<string>('');

    const [hasError, setHasError] = useState<boolean>(false);
    const [screenTitle, setScreenTitle] = useState<string>('');
    const [focusedVodItem, setFocusedVodItem] = useState<PlayableVodItem>();

    const columnLimit = useTivoSelector(
        (state: RootState) => state.grid[GridTypes.GALLERY]?.columnLimit || 0,
    );
    const ghostMode = useTivoSelector(
        (state: RootState) => state.grid[GridTypes.GALLERY]?.ghostMode || false,
    );
    const highlightedItem = useTivoSelector(
        (state: RootState) => state.grid[GridTypes.GALLERY]?.focusedItem || { row: 0, column: 0 },
    );
    const { isActive: isAntiBurnOsdActive } = useTivoSelector(
        (state: RootState) => state.antiburnOsd,
    );
    const rowLimit = useTivoSelector(
        (state: RootState) => state.grid[GridTypes.GALLERY]?.rowLimit || 0,
    );
    const url = useTivoSelector((state: RootState) => state.urlChangeEvents.url);

    const onNetworkStateChangeCallback = () => {
        const isDisabled = NetworkManager.isNetworkDisconnected();

        if (isDisabled) {
            dispatch(showNetworkWhisperGalleryScreen());
        }
    };

    const onCursorStateChangeCallback = (state: boolean) => {
        if (state === false) {
            dispatch(clearActiveInteractiveComponent());
            dispatch(gallerySetCaretInvisible());
        }
    };

    const screenProperties = ScreenController.getInstance()
        .getScreen<TGalleryScreenState>()
        .getProperties()!;

    const { screenType } = screenProperties;
    const galleryScreenInstance = new GalleryScreenComponent(screenType);

    const itemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const loadingState = useTivoSelector(
        (state: RootState) => state.grid[GridTypes.GALLERY]?.loadingState,
    );
    const galleryItems = useTivoSelector(
        (state: any) => state.grid[GridTypes.GALLERY]?.items || [],
    );

    useEffect(() => {
        if (!isAntiBurnOsdActive) {
            const { parentVodItemId } = screenProperties;
            Log.d(TAG, 'Refreshing gallery items as Antiburn is hidden.');
            Diagnostics.debug(TAG, 'Refreshing gallery items as Antiburn is hidden.');
            galleryScreenInstance.getInitialGalleryPage(parentVodItemId)?.then((result) => {
                if (result) {
                    handleGalleryPageResult(result, highlightedItem.row, highlightedItem.column);
                }
            });
        }
    }, [isAntiBurnOsdActive]);

    // Scroll the highlighted item into view
    useEffect(() => {
        const focusedIndex = highlightedItem.row * columnLimit + highlightedItem.column;
        const el = itemRefs.current[focusedIndex];
        if (el) {
            el.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'nearest',
            });
        }
        setFocusedVodItem(galleryItems[focusedIndex]);
    }, [highlightedItem, columnLimit, galleryItems]);

    const GalleryEpisodeTileLayout = (
        item: EpisodeVodItem,
        index: number,
        displayTitle: string,
        highlighted: boolean = false,
    ) => {
        const mediaAttributes = galleryScreenInstance.getMediaAttributes(item);
        return (
            <div
                ref={(el) => {
                    itemRefs.current[index] = el;
                }}
                className={'episode-gallery-item'}
            >
                <GalleryItem
                    key={`gridItem-${index}`}
                    objectId={item.digObjectId || ''}
                    type={item.vodItemType}
                    isHighlighted={highlighted}
                    percentWatched={item.percentWatched}
                    isGhostModeEnabled={ghostMode}
                    title={displayTitle}
                />
                <div className={`media-attributes ${highlighted ? 'media-highlighted' : ''}`}>
                    <span className="season-episode-info">{mediaAttributes.seasonEpisode}</span>
                    {!item.episodeNumber && !item.seasonNumber && (
                        <span className="air-date-info">{mediaAttributes.airDate}</span>
                    )}
                    {mediaAttributes.resolutionIcon && (
                        <div className={`video-resolution ${mediaAttributes.resolutionIcon}`} />
                    )}
                    {mediaAttributes.expiryIcon && (
                        <div className={`icon-expiry ${mediaAttributes.expiryIcon}`} />
                    )}
                </div>
            </div>
        );
    };

    const GalleryMovieTileLayout = (
        item: MovieVodItem,
        index: number,
        highlighted: boolean = false,
    ): React.ReactNode => {
        const movieMediaAttributes = galleryScreenInstance.getMediaAttributes(item);
        return (
            <div ref={(el) => (itemRefs.current[index] = el)} className="movie-gallery-item">
                <GalleryItem
                    objectId={item.digObjectId || ''}
                    type={item.vodItemType}
                    isHighlighted={highlighted}
                    percentWatched={item.percentWatched}
                    isGhostModeEnabled={ghostMode}
                    title={item.title || ''}
                />
                <div className={`media-attributes ${highlighted ? 'media-highlighted' : ''}`}>
                    {movieMediaAttributes.resolutionIcon && (
                        <div
                            className={`video-resolution ${movieMediaAttributes.resolutionIcon}`}
                        />
                    )}
                    {movieMediaAttributes.expiryIcon && (
                        <div className={`icon-expiry ${movieMediaAttributes.expiryIcon}`} />
                    )}
                </div>
            </div>
        );
    };

    const generateTileLayout = (
        item: PlayableVodItem,
        index: number,
        totalColumns: number,
    ): React.ReactNode => {
        const row = Math.floor(index / totalColumns);
        const column = index % totalColumns;

        const isHighlighted = highlightedItem.row === row && highlightedItem.column === column;

        const { vodItemType, title } = item;
        const galleryTileVodItemType = vodItemType?.toLowerCase();

        const getHighlightClass = () => {
            if (isHighlighted && ghostMode) return 'gallery-grid-ghost-highlight';
            if (isHighlighted) return 'gallery-grid-highlight';
            return '';
        };

        const MAX_SINGLE_WORD_LENGTH = 18;
        const isSingleWord = title && !title.trim().includes(' ');
        const displayTitle =
            isSingleWord && title.length > MAX_SINGLE_WORD_LENGTH
                ? `${title.slice(0, MAX_SINGLE_WORD_LENGTH - 3)}..`
                : title;

        if (screenType?.toLowerCase() === ScreenType.PosterBrowseMixed?.toLowerCase()) {
            // For PosterBrowseMixed, render based on individual item type
            switch (galleryTileVodItemType) {
                case VodItemType.EpisodeVodItem?.toLowerCase():
                case VodItemType.SpecialVodItem?.toLowerCase():
                case VodItemType.UnifiedCollectionVodItem?.toLowerCase():
                    return (
                        <Mousetrap key={`grid_item_${index}`} value={`grid_item_${row}_${column}`}>
                            {GalleryEpisodeTileLayout(
                                item,
                                index,
                                displayTitle || '',
                                isHighlighted,
                            )}
                        </Mousetrap>
                    );
                case VodItemType.FolderVodItem?.toLowerCase():
                    return (
                        <Mousetrap key={`grid_item_${index}`} value={`grid_item_${row}_${column}`}>
                            <div
                                key={`gridItem-${index}`}
                                ref={(el) => {
                                    itemRefs.current[index] = el;
                                }}
                                className={`folder-gallery-item ${getHighlightClass()}`}
                            >
                                <GalleryItem
                                    key={`gridItem-${index}`}
                                    objectId={item.digObjectId || ''}
                                    type={item.vodItemType}
                                    isHighlighted={isHighlighted}
                                    percentWatched={item.percentWatched}
                                    isGhostModeEnabled={ghostMode}
                                    title={displayTitle || ''}
                                />
                            </div>
                        </Mousetrap>
                    );
                case VodItemType.MovieVodItem?.toLowerCase():
                    return (
                        <Mousetrap key={`grid_item_${index}`} value={`grid_item_${row}_${column}`}>
                            {GalleryEpisodeTileLayout(
                                item,
                                index,
                                displayTitle || '',
                                isHighlighted,
                            )}
                        </Mousetrap>
                    );
                default:
                    return '';
            }
        } else if (
            screenType?.toLocaleLowerCase() === ScreenType.PosterBrowseMovie?.toLowerCase()
        ) {
            // For PosterBrowseMovie, always render movie layout
            return (
                <Mousetrap key={`grid_item_${index}`} value={`grid_item_${row}_${column}`}>
                    {GalleryMovieTileLayout(item, index, isHighlighted)}
                </Mousetrap>
            );
        }

        // Fallback for other screen types
        return '';
    };

    const handleGalleryPageResult = (
        result: TGalleryGridActivePageResponse,
        row: number,
        column: number,
    ) => {
        if (!result) return;

        const { response, gridParams } = result;

        if (response?.errorCode) {
            dispatch(showErrorOverlayGalleryScreen(response.errorCode));
            setHasError(response.hasError ?? false);
            return;
        }

        if (response.items) {
            dispatch(
                setGridItemsAction(GridTypes.GALLERY, {
                    items: response.items,
                    focusedItem: { row, column },
                    rowLimit: gridParams.rowLimit,
                    columnLimit: gridParams.colLimit,
                    ghostMode: false,
                    isActive: true,
                }),
            );
        }
    };

    // eslint-disable-next-line consistent-return
    useEffect(() => {
        const refNetworkManager = NetworkManager.subscribe(onNetworkStateChangeCallback);
        const refCursormanager = CursorManager.subscribe(onCursorStateChangeCallback);

        if (url.includes(galleryUrl)) {
            if (KeyManager.getInstance().isKeyIgnoring())
                KeyManager.getInstance().stopKeyIgnoring();
            Diagnostics.info(TAG, 'GalleryScreen mounted.');
            Log.i(TAG, 'GalleryScreen mounted.');
            const { parentVodItemId, row, column, title } = screenProperties;
            setScreenTitle(title || LocaleKeys.TITLE_NOT_AVAILABLE);
            dispatch(setGridItemFocusAction(GridTypes.GALLERY, { row, column }));
            parentVodItemIdRef.current = parentVodItemId || '';
            galleryScreenInstance
                .getInitialGalleryPage(parentVodItemIdRef.current)
                .then((result) => {
                    if (result) {
                        handleGalleryPageResult(result, row, column);
                    }
                });
        }

        return () => {
            NetworkManager.unsubscribe(refNetworkManager);
            CursorManager.unsubscribe(refCursormanager);
            setScreenTitle('');
            setHasError(false);
            galleryScreenInstance.reset();
            dispatch(resetGrid(GridTypes.GALLERY));
            dispatch(hideWhisper());
            dispatch(hideOverlayDialog());
            Diagnostics.info(TAG, 'GalleryScreen unmounted.');
            Log.i(TAG, 'GalleryScreen unmounted.');
        };
    }, [url]);

    const gridChildren =
        galleryItems.length === 0 ? (
            <div />
        ) : (
            galleryItems?.map((item: PlayableVodItem, index: number) =>
                generateTileLayout(
                    item,
                    index,
                    screenType === ScreenType.PosterBrowseMixed ? 3 : 5,
                ),
            )
        );

    return (
        <div className="gallery-container">
            <ScreenHeader title={screenTitle || ''} clockType={ClockTypes.DEFAULT_CLOCK} />
            <div className="gallery-outer-wrapper">
                {GalleryScreenComponent.getNoItems() && !hasError && (
                    <div className="gallery-no-items-message">
                        {Locale.getString(LocaleKeys.GALLERY_SCREEN_EMPTY_GRID_MESSAGE)}
                    </div>
                )}
                {!GalleryScreenComponent.getNoItems() &&
                    !hasError &&
                    (loadingState ? (
                        <GalleryGridSkeleton
                            count={GalleryScreenComponent.getItemsLeftInGallery()}
                            columns={columnLimit}
                            className="gallery-grid"
                            vodType={
                                galleryItems.length > 0 ? galleryItems[0]?.vodItemType : undefined
                            }
                        />
                    ) : (
                        <div className="gallery-scrollable-wrapper">
                            <Grid
                                columns={columnLimit}
                                rows={rowLimit}
                                horizontalGap="0.83vw" // 16px
                                verticalGap="1.48vh" // 16px
                                className="gallery-grid"
                                padding="0.31vw" // 6px
                            >
                                {gridChildren}
                            </Grid>
                        </div>
                    ))}
            </div>
            <div className="gallery-footer-shadow" />
            <Whisper message="" />
            <div className="vod-gallery-preview-container">
                {isVodPreviewEnabled && focusedVodItem && (
                    <VodPreview
                        screenType={VodScreenType.VOD_GALLERY_SCREEN}
                        item={focusedVodItem}
                    />
                )}
            </div>
            {isAntiBurnOsdActive && <AntiburnOsd />}
            {DeviceModel.isWebOsPlatform() && CursorManager.isCursorAppeared() ? (
                <InteractiveLayer />
            ) : null}
        </div>
    );
};

export default GalleryScreen;
